





























































import { cloneDeep, debounce, filter, flow, isEmpty, isEqual, map, xorWith } from 'lodash';
import { BDropdown, VBTooltip } from 'bootstrap-vue';

import ZenButton from '@reciprocity/button';

import FiltersFilter from './components/Filter';
import { ZenFilterAbbreviated } from './types';
import { OBJECT_TYPE_ATTRIBUTES_QUERY } from './components/queries';

const DEFAULT_FILTER = {
  value: {
    attribute: null,
    operator: null,
    value: '',
  },
  meta: {
    pristine: true,
    valid: false,
  },
};

export default {
  name: 'ListingFilters',
  components: {
    BDropdown,

    ZenButton,

    // "Filter" is a reserved word in Vue templates
    FiltersFilter,
  },
  directives: {
    BTooltip: VBTooltip,
  },
  props: {
    objectType: {
      // In ModelMetaSchema.modelSingular form
      type: String,
      required: true,
    },
    activeColumns: {
      type: Array,
      required: true,
    },
    activeFilters: {
      type: Array as () => ZenFilterAbbreviated[],
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      initialState: true,
      shown: false,
      activeFiltersData: [DEFAULT_FILTER],
    };
  },
  computed: {
    hasFilterableAttributes() {
      return filter(this.objectTypeAttributes, attr => attr.filterable).length > 0;
    },
    filterButtonDisabled() {
      return (this.disabled && !this.shown) || !this.hasFilterableAttributes;
    },
    defaultActiveFilters() {
      return [DEFAULT_FILTER];
    },
    removable(): boolean {
      return (!!this.activeFiltersData && this.activeFiltersData.length > 1) || !this.pristine;
    },
    filtersToEmit() {
      const emitFilter = filter => filter.meta.valid;
      // TODO: replace old f:o:v: syntax with new
      const filterEmitMapTo = filter => ({
        f: filter.value.attribute,
        o: filter.value.operator,
        v: filter.value.value,
      });

      const toEmit = flow(
        filters => filter(filters, emitFilter),
        filters => map(filters, filterEmitMapTo)
      );

      return toEmit(this.activeFiltersData);
    },
    pristine() {
      const pristineFilter = filter => filter.meta.pristine;
      const pristineFilters = filters => filter(filters, pristineFilter);

      return pristineFilters(this.activeFiltersData).length;
    },
    dirty(): boolean {
      return this.pristine !== this.activeFiltersData.length;
    },
  },
  watch: {
    objectType: function() {
      this.activeFiltersData = [DEFAULT_FILTER];
      this.initialState = true;
    },
    activeFilters: {
      handler: function(newActiveFilters) {
        this.setActiveFilters(newActiveFilters);
      },
    },
    filtersToEmit: {
      deep: true,
      handler: function(newFiltersToEmit, oldFiltersToEmit) {
        // Checks if arrays of objects are equal
        const areFiltersEqual = (arr1, arr2) => isEmpty(xorWith(arr1, arr2, isEqual));

        if (areFiltersEqual(newFiltersToEmit, oldFiltersToEmit)) return;

        this.emitFilters(newFiltersToEmit);
      },
    },
  },
  methods: {
    setActiveFilters(newActiveFilters: ZenFilterAbbreviated[]): void {
      if (!newActiveFilters.length || !this.initialState) return;
      this.initialState = false;

      const mapToInitialFilter = (filter: ZenFilterAbbreviated) => ({
        value: {
          attribute: filter.f,
          operator: filter.o,
          value: filter.v,
        },
        meta: {
          pristine: false,
          /**
           * TODO:
           * We are assuming since it was valid when it was saved, it remains valid.
           * For now this is fine, however there are cases (i.e. an object being deleted)
           * where revalidation will be necessary.
           */
          valid: true,
        },
      });

      this.activeFiltersData = map(newActiveFilters, mapToInitialFilter);
    },
    onShow(): void {
      this.setActiveFilters(this.activeFilters);
      this.shown = true;
    },
    onHide(): void {
      this.shown = false;
    },
    setDefaultFilters(): void {
      this.activeFiltersData = cloneDeep(this.defaultActiveFilters);
    },
    addFilter(): void {
      this.activeFiltersData.push(DEFAULT_FILTER);
    },
    onChange(index: number, filterAndMeta): void {
      this.activeFiltersData[index] = filterAndMeta;
      this.activeFiltersData = cloneDeep(this.activeFiltersData);
    },
    onRemove(index: number): void {
      if (this.activeFiltersData.length == 1) {
        this.removeFilters();
      } else {
        this.removeFilter(index);
      }
    },
    removeFilter(index: number): void {
      this.activeFiltersData.splice(index, 1);
    },
    removeFilters(): void {
      this.setDefaultFilters();
    },
    emitFilters: debounce(function(filtersToEmit) {
      this.$emit('filtersChange', filtersToEmit);
    }, 500),
  },
  apollo: {
    objectTypeAttributes: {
      query: OBJECT_TYPE_ATTRIBUTES_QUERY,
      variables() {
        return {
          type: this.objectType,
          context: 'SOR_LISTING',
        };
      },
      update: data => data.objectType.attrs,
    },
  },
};
